import {useEffect,useMemo,useState} from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography
} from "@mui/material";
import {
  AccessTimeRounded,
  CalendarMonthRounded,
  CloseRounded,
  LocationOnOutlined,
  SportsSoccerRounded
} from "@mui/icons-material";
import scoreBackground from "../../static/Matches/score.webp";

const API_BASE="/matches/public/tournaments/nexus-cup-2026";

const COLORS={
  navy:"#06132D",
  muted:"#B8C8E4"
};

const formatDate=value=>{
  if(!value) return "Fecha por definir";

  const date=new Date(`${value}T12:00:00`);

  if(Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("es-EC",{
    day:"numeric",
    month:"short",
    year:"numeric"
  }).format(date);
};

const hasScore=match=>(
  match?.score_local!==null&&
  match?.score_local!==undefined&&
  match?.score_visitor!==null&&
  match?.score_visitor!==undefined
);

const minuteLabel=minute=>{
  if(minute===null||minute===undefined) return "—";
  return `${minute}'`;
};

const abbreviateName=name=>{
  if(!name) return "Jugador";

  const words=name.trim().split(/\s+/);

  if(words.length<2) return name;

  return `${words[0].charAt(0)}. ${words.slice(1).join(" ")}`;
};

const getEventType=event=>{
  if(event.kind==="GOAL") return "GOAL";
  return event.event_type||"CARD";
};

const groupEvents=events=>{
  const map=new Map();

  for(const event of events){
    const playerKey=event.player?.slug||event.player?.name||"sin-jugador";
    const eventType=getEventType(event);
    const key=`${playerKey}-${eventType}`;

    if(!map.has(key)){
      map.set(key,{
        ...event,
        eventType,
        minutes:[],
        count:0
      });
    }

    const item=map.get(key);

    item.count+=1;

    if(event.minute!==null&&event.minute!==undefined){
      item.minutes.push(event.minute);
    }
  }

  return Array.from(map.values())
    .map(event=>({
      ...event,
      minutes:event.minutes.sort((a,b)=>Number(a)-Number(b))
    }))
    .sort((a,b)=>{
      const firstA=a.minutes.length?Number(a.minutes[0]):999;
      const firstB=b.minutes.length?Number(b.minutes[0]):999;
      return firstA-firstB;
    });
};

function TeamLogo({team}){
  const [failed,setFailed]=useState(false);

  useEffect(()=>{
    setFailed(false);
  },[team?.logo]);

  return(
    <Box
      sx={{
        width:{xs:64,sm:96,md:124},
        height:{xs:64,sm:96,md:124},
        flexShrink:0,
        display:"flex",
        alignItems:"center",
        justifyContent:"center"
      }}
    >
      {team?.logo&&!failed?(
        <Box
          component="img"
          src={team.logo}
          alt={team.name||"Equipo"}
          onError={()=>setFailed(true)}
          sx={{
            display:"block",
            width:"100%",
            height:"100%",
            objectFit:"contain"
          }}
        />
      ):(
        <SportsSoccerRounded
          sx={{
            fontSize:{xs:38,md:60},
            color:"rgba(255,255,255,.65)"
          }}
        />
      )}
    </Box>
  );
}

function EventSymbol({event}){
  if(event.kind==="GOAL"){
    const label=event.goal_type==="OWN_GOAL"
      ?"Autogol"
      :event.goal_type==="PENALTY"
        ?"Gol de penal"
        :"Gol";

    return(
      <SportsSoccerRounded
        titleAccess={label}
        sx={{
          fontSize:{xs:15,sm:18},
          color:event.goal_type==="OWN_GOAL"
            ?"#FFB8BF"
            :"#FFFFFF",
          flexShrink:0
        }}
      />
    );
  }

  const secondYellow=event.event_type==="SECOND_YELLOW";
  const red=event.event_type==="RED";

  const label=red
    ?"Tarjeta roja"
    :secondYellow
      ?"Segunda tarjeta amarilla"
      :"Tarjeta amarilla";

  return(
    <Box
      title={label}
      role="img"
      aria-label={label}
      sx={{
        position:"relative",
        width:{xs:14,sm:17},
        height:{xs:19,sm:22},
        flexShrink:0
      }}
    >
      {secondYellow&&(
        <Box
          sx={{
            position:"absolute",
            width:{xs:10,sm:12},
            height:{xs:15,sm:17},
            top:0,
            left:0,
            borderRadius:"2px",
            backgroundColor:"#F5CB40"
          }}
        />
      )}

      <Box
        sx={{
          position:"absolute",
          width:{xs:10,sm:12},
          height:{xs:15,sm:17},
          top:secondYellow?3:2,
          left:secondYellow?3:2,
          borderRadius:"2px",
          backgroundColor:red?"#E54853":"#F5CB40"
        }}
      />
    </Box>
  );
}

function EventLine({event,side}){
  const playerName=event.player?.name||"Jugador";

  const minutes=event.minutes.length
    ?event.minutes.map(minuteLabel).join(", ")
    :"—";

  const isVisitor=side==="visitor";

  return(
    <Box
      sx={{
        display:"flex",
        alignItems:"center",
        gap:{xs:.5,sm:1,md:1.5},
        minWidth:0,
        minHeight:{xs:43,sm:50},
        py:{xs:.9,sm:1.3},
        borderBottom:"1px solid rgba(255,255,255,.14)",
        "&:last-child":{
          borderBottom:"none"
        }
      }}
    >
      {!isVisitor&&(
        <Box
          sx={{
            flexShrink:0,
            maxWidth:{xs:57,sm:90}
          }}
        >
          <Typography
            sx={{
              fontSize:{xs:10,sm:12},
              fontWeight:600,
              color:"#B9CFFA",
              fontVariantNumeric:"tabular-nums",
              lineHeight:1.4
            }}
          >
            {minutes}
          </Typography>
        </Box>
      )}

      {isVisitor&&(
        <EventSymbol event={event}/>
      )}

      <Box
        sx={{
          flex:1,
          minWidth:0
        }}
      >
        <Typography
          sx={{
            display:{xs:"block",sm:"none"},
            color:"#FFFFFF",
            fontSize:10,
            fontWeight:500,
            lineHeight:1.4,
            textAlign:isVisitor?"right":"left",
            overflowWrap:"anywhere"
          }}
        >
          {abbreviateName(playerName)}
        </Typography>

        <Typography
          sx={{
            display:{xs:"none",sm:"block"},
            color:"#FFFFFF",
            fontSize:{sm:12,md:14},
            fontWeight:500,
            lineHeight:1.4,
            textAlign:isVisitor?"right":"left",
            overflowWrap:"anywhere"
          }}
        >
          {playerName}
        </Typography>
      </Box>

      {!isVisitor&&(
        <EventSymbol event={event}/>
      )}

      {isVisitor&&(
        <Box
          sx={{
            flexShrink:0,
            maxWidth:{xs:57,sm:90}
          }}
        >
          <Typography
            sx={{
              fontSize:{xs:10,sm:12},
              fontWeight:600,
              color:"#B9CFFA",
              fontVariantNumeric:"tabular-nums",
              lineHeight:1.4,
              textAlign:"right"
            }}
          >
            {minutes}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function TeamEvents({events,side}){
  const groupedEvents=useMemo(
    ()=>groupEvents(events),
    [events]
  );

  return(
    <Box
      sx={{
        minWidth:0,
        width:"100%",
        borderTop:"1px solid rgba(255,255,255,.20)"
      }}
    >
      {groupedEvents.map((event,index)=>(
        <EventLine
          key={`${event.kind}-${event.player?.slug}-${event.eventType}-${index}`}
          event={event}
          side={side}
        />
      ))}
    </Box>
  );
}

function Scoreboard({match,detail,loading,error}){
  const events=detail?.timeline||[];

  const localEvents=useMemo(
    ()=>events.filter(event=>event.side==="local"),
    [events]
  );

  const visitorEvents=useMemo(
    ()=>events.filter(event=>event.side==="visitor"),
    [events]
  );

  const hasEvents=localEvents.length>0||visitorEvents.length>0;
  const showScore=hasScore(match);

  const played=[
    "LIVE",
    "FINISHED",
    "SUSPENDED"
  ].includes(match.status);

  return(
    <Box
      sx={{
        position:"relative",
        minHeight:{xs:360,sm:470,md:510},
        backgroundColor:COLORS.navy,
        backgroundImage:`
          linear-gradient(
            180deg,
            rgba(2,10,28,.32) 0%,
            rgba(2,10,28,.59) 36%,
            rgba(2,10,28,.83) 100%
          ),
          url(${scoreBackground})
        `,
        backgroundPosition:"center",
        backgroundSize:"cover",
        px:{xs:1.5,sm:4,md:6},
        pt:{xs:5.5,sm:6.5,md:7},
        pb:{xs:3,sm:5,md:6}
      }}
    >
      <Box
        sx={{
          position:"relative",
          maxWidth:1100,
          mx:"auto"
        }}
      >
        <Box
          sx={{
            display:"grid",
            gridTemplateColumns:"minmax(0,1fr) auto minmax(0,1fr)",
            alignItems:"center",
            columnGap:{xs:.5,sm:3,md:6},
            maxWidth:930,
            mx:"auto"
          }}
        >
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={{xs:1,md:1.7}}
            sx={{minWidth:0}}
          >
            <TeamLogo team={match.local}/>

            <Typography
              sx={{
                fontSize:{xs:11,sm:17,md:20},
                fontWeight:600,
                color:"#FFFFFF",
                textAlign:"center",
                lineHeight:1.35,
                overflowWrap:"anywhere"
              }}
            >
              {match.local?.name}
            </Typography>
          </Stack>

          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{minWidth:0}}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={{xs:.5,sm:1.5,md:2}}
            >
              <Typography
                sx={{
                  fontSize:{xs:42,sm:65,md:83},
                  fontWeight:600,
                  letterSpacing:"-.05em",
                  lineHeight:1,
                  color:"#FFFFFF",
                  fontVariantNumeric:"tabular-nums"
                }}
              >
                {showScore?match.score_local:"-"}
              </Typography>

              <Typography
                sx={{
                  fontSize:{xs:24,sm:33,md:41},
                  color:"#B7CFFF",
                  fontWeight:400
                }}
              >
                :
              </Typography>

              <Typography
                sx={{
                  fontSize:{xs:42,sm:65,md:83},
                  fontWeight:600,
                  letterSpacing:"-.05em",
                  lineHeight:1,
                  color:"#FFFFFF",
                  fontVariantNumeric:"tabular-nums"
                }}
              >
                {showScore?match.score_visitor:"-"}
              </Typography>
            </Stack>

            <Typography
              sx={{
                color:"#C9D9F3",
                fontSize:{xs:10,sm:12},
                fontWeight:500,
                textAlign:"center",
                whiteSpace:"nowrap"
              }}
            >
              {match.status==="FINISHED"
                ?"Tiempo completo"
                :match.status==="LIVE"
                  ?"En vivo"
                  :match.status==="SCHEDULED"
                    ?"Por disputarse"
                    :match.status==="SUSPENDED"
                      ?"Suspendido"
                      :"Cancelado"}
            </Typography>
          </Stack>

          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={{xs:1,md:1.7}}
            sx={{minWidth:0}}
          >
            <TeamLogo team={match.visitor}/>

            <Typography
              sx={{
                fontSize:{xs:11,sm:17,md:20},
                fontWeight:600,
                color:"#FFFFFF",
                textAlign:"center",
                lineHeight:1.35,
                overflowWrap:"anywhere"
              }}
            >
              {match.visitor?.name}
            </Typography>
          </Stack>
        </Box>

        <Box
          sx={{
            width:"100%",
            maxWidth:930,
            mx:"auto",
            mt:{xs:3,sm:5},
            pt:{xs:1.5,sm:2.5},
            borderTop:"1px solid rgba(255,255,255,.2)"
          }}
        >
          {loading?(
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={1.5}
              sx={{py:4}}
            >
              <CircularProgress
                size={25}
                sx={{color:"#D2E3FF"}}
              />

              <Typography
                sx={{
                  fontSize:12,
                  color:COLORS.muted
                }}
              >
                Cargando acontecimientos...
              </Typography>
            </Stack>
          ):error?(
            <Typography
              sx={{
                py:3,
                textAlign:"center",
                fontSize:12,
                color:"#FFBCC4"
              }}
            >
              {error}
            </Typography>
          ):hasEvents?(
            <Box
              sx={{
                display:"grid",
                gridTemplateColumns:"repeat(2,minmax(0,1fr))",
                columnGap:{xs:1.5,sm:4,md:6},
                alignItems:"start"
              }}
            >
              <TeamEvents
                events={localEvents}
                side="local"
              />

              <TeamEvents
                events={visitorEvents}
                side="visitor"
              />
            </Box>
          ):(
            <Typography
              sx={{
                textAlign:"center",
                color:"#C3D1E9",
                fontSize:{xs:11,sm:12},
                lineHeight:1.7,
                py:2
              }}
            >
              {played
                ?"Sin goles ni tarjetas registrados."
                :"Los acontecimientos aparecerán al disputarse el encuentro."}
            </Typography>
          )}
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          useFlexGap
          flexWrap="wrap"
          gap={{xs:1.5,sm:3}}
          sx={{
            maxWidth:900,
            mx:"auto",
            mt:{xs:3,sm:5},
            pt:2.5,
            borderTop:"1px solid rgba(255,255,255,.16)"
          }}
        >
          <Stack direction="row" alignItems="center" spacing={.7}>
            <CalendarMonthRounded
              sx={{
                fontSize:16,
                color:"#BDD2F8"
              }}
            />

            <Typography
              sx={{
                color:"#EEF4FF",
                fontSize:11
              }}
            >
              {formatDate(match.date)}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={.7}>
            <AccessTimeRounded
              sx={{
                fontSize:16,
                color:"#BDD2F8"
              }}
            />

            <Typography
              sx={{
                color:"#EEF4FF",
                fontSize:11
              }}
            >
              {match.start_time||"Por definir"}
            </Typography>
          </Stack>

          {match.venue&&(
            <Stack direction="row" alignItems="center" spacing={.7}>
              <LocationOnOutlined
                sx={{
                  fontSize:16,
                  color:"#BDD2F8"
                }}
              />

              <Typography
                sx={{
                  color:"#EEF4FF",
                  fontSize:11
                }}
              >
                {match.venue.name}
              </Typography>
            </Stack>
          )}
        </Stack>

        {!loading&&!error&&match.referees?.length>0&&(
          <Box
            sx={{
              maxWidth:850,
              mx:"auto",
              mt:2.2
            }}
          >
            <Typography
              sx={{
                fontSize:11,
                color:"#BDD0F0",
                textAlign:"center",
                lineHeight:1.8
              }}
            >
              {match.referees.map(referee=>
                `${referee.role_display}: ${referee.name}`
              ).join("  ·  ")}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function MatchDetailsSection({
  open=false,
  match=null,
  onClose
}){
  const [detail,setDetail]=useState(null);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");

  useEffect(()=>{
    if(!open||!match?.code){
      setDetail(null);
      setLoading(false);
      setError("");
      return;
    }

    const controller=new AbortController();

    const loadDetail=async()=>{
      setLoading(true);
      setDetail(null);
      setError("");

      try{
        const response=await fetch(
          `${API_BASE}/matches/${match.code}/`,
          {signal:controller.signal}
        );

        if(!response.ok){
          throw new Error(
            "No se pudo obtener la información del encuentro."
          );
        }

        const result=await response.json();

        if(!controller.signal.aborted){
          setDetail(result);
        }
      }catch(err){
        if(err.name!=="AbortError"&&!controller.signal.aborted){
          setError(
            err.message||"No se pudo cargar el partido."
          );
        }
      }finally{
        if(!controller.signal.aborted){
          setLoading(false);
        }
      }
    };

    loadDetail();

    return()=>controller.abort();
  },[open,match?.code]);

  const currentMatch=detail?.match||match;

  return(
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      aria-label="Información del partido"
      PaperProps={{
        sx:{
          width:{
            xs:"calc(100% - 16px)",
            sm:"calc(100% - 48px)"
          },
          maxWidth:1120,
          m:{xs:1,sm:3},
          maxHeight:{
            xs:"calc(100dvh - 16px)",
            sm:"calc(100dvh - 48px)"
          },
          borderRadius:{xs:"18px",sm:"24px"},
          overflow:"hidden",
          backgroundColor:COLORS.navy,
          boxShadow:"none",
          border:"1px solid rgba(169,196,238,.25)"
        }
      }}
      BackdropProps={{
        sx:{
          backgroundColor:"rgba(2,8,22,.84)",
          backdropFilter:"blur(6px)",
          WebkitBackdropFilter:"blur(6px)"
        }
      }}
    >
      <Box
        sx={{
          position:"relative",
          width:"100%",
          overflowY:"auto",
          scrollbarWidth:"thin",
          scrollbarColor:"#34517B #07162F"
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label="Cerrar detalles del partido"
          sx={{
            position:"absolute",
            top:{xs:12,sm:18},
            right:{xs:12,sm:18},
            zIndex:5,
            width:38,
            height:38,
            color:"#FFFFFF",
            border:"1px solid rgba(255,255,255,.23)",
            backgroundColor:"rgba(5,16,40,.68)",
            backdropFilter:"blur(8px)",
            "&:hover":{
              backgroundColor:"rgba(28,62,119,.85)"
            }
          }}
        >
          <CloseRounded fontSize="small"/>
        </IconButton>

        {currentMatch&&(
          <Scoreboard
            match={currentMatch}
            detail={detail}
            loading={loading}
            error={error}
          />
        )}
      </Box>
    </Dialog>
  );
}